import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import "../styles/Purchases.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUndo,
    faSearch,
    faFileInvoice,
    faCalendarAlt,
    faUser,
    faPhone,
    faMapMarkerAlt,
    faBox,
    faTag,
    faBarcode,
    faRupeeSign,
    faBalanceScale,
    faDollarSign,
    faCalculator,
    faExclamationTriangle,
    faTrash,
    faSave,
    faTimes,
    faSpinner,
    faCheckCircle,
    faExclamationCircle,
    faCreditCard,
    faMoneyBill,
    faWallet,
    faUniversity,
    faPercent,
    faReceipt,
    faChevronDown,
    faChevronUp,
    faBoxes,
    faHistory,
    faShoppingCart,
    faStore,
    faPlus,
    faMinus,
    faFilter,
    faPrint,
    faFilePdf,
    faQrcode,
    faTimesCircle,
    faIdCard,
    faLock,
    faEye,
    faEyeSlash
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// Translation Utility
// ==============================================
const createReturnTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_return_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Return Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_return_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        processReturn: 'Process Return',
        returnItems: 'Return Items',
        returnInformation: 'Return Information',
        returnNumber: 'Return Number',
        returnDate: 'Return Date',
        originalInvoice: 'Original Invoice',
        originalBillNumber: 'Original Bill Number',
        originalDate: 'Original Date',
        customerDetails: 'Customer Details',
        customerName: 'Customer Name',
        customerPhone: 'Phone Number',
        customerAddress: 'Address',
        searchInvoice: 'Search Invoice',
        searchPlaceholder: 'Enter Invoice',
        invoiceFound: 'Invoice found',
        invoiceNotFound: 'Invoice not found',
        itemsToReturn: 'Items to Return',
        productName: 'Product',
        batchNumber: 'Batch',
        unit: 'Unit',
        price: 'Price',
        quantityToReturn: 'Qty to Return',
        available: 'Available',
        total: 'Total',
        remove: 'Remove',
        returnCalculations: 'Return Calculations',
        subtotal: 'Subtotal',
        tax: 'Tax',
        discount: 'Discount',
        grandTotal: 'Grand Total',
        refundAmount: 'Refund Amount',
        refundMethod: 'Refund Method',
        cash: 'Cash',
        card: 'Card',
        upi: 'UPI',
        bankTransfer: 'Bank Transfer',
        wallet: 'Wallet',
        returnReason: 'Return Reason',
        notes: 'Notes',
        processReturnBtn: 'Process Return',
        clear: 'Clear',
        saving: 'Processing...',
        loading: 'Loading...',
        returnProcessed: 'Return processed successfully!',
        failedProcess: 'Failed to process return',
        networkError: 'Network error occurred',
        noShopSelected: 'No shop selected. Please select a shop first.',
        itemsRequired: 'At least one item is required for return',
        quantityExceedsAvailable: 'Quantity exceeds available stock',
        invalidQuantity: 'Please enter a valid quantity',
        returnReasonRequired: 'Return reason is required',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        todayReturns: "Today's Returns",
        totalReturns: 'Total Returns',
        totalAmount: 'Total Amount',
        totalRefund: 'Total Refund',
        printReceipt: 'Print Receipt',
        returnReceipt: 'RETURN RECEIPT',
        paymentDetails: 'Payment Details',
        thankYou: 'Thank you for your business!',
        footer: 'Powered by OurZest',
        businessInfo: 'Business Information',
        cashier: 'Cashier',
        transactionId: 'Transaction ID',
        poweredBy: 'Powered by OurZest',
        itemsReturned: 'Items Returned',
        printing: 'Printing receipt...',
        printSuccess: 'Receipt sent to printer',
        close: 'Close',
        print: 'Print',
        shop: 'Shop'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    const notifyListeners = (lang) => {
        translationListeners.forEach(callback => callback(lang));
    };

    const checkLanguageChange = () => {
        const currentLang = localStorage.getItem('ourzest_language') || 'en';
        notifyListeners(currentLang);
    };

    const startLanguageCheck = () => {
        checkLanguageChange();
        const intervalId = setInterval(checkLanguageChange, 500);
        window.addEventListener('storage', checkLanguageChange);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', checkLanguageChange);
        };
    };

    loadFromCache();

    return {
        getTranslations,
        translateText,
        addListener,
        removeListener,
        startLanguageCheck
    };
};

const returnTranslator = createReturnTranslator();

// ==============================================
// Toast Notification Component (Enhanced)
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationCircle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations?.success || 'Success';
            case 'error':
                return translations?.error || 'Error';
            case 'warning':
                return translations?.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Return Items Table Component (Enhanced with translations)
// ==============================================
const ReturnItemsTable = ({
    items,
    onQuantityChange,
    onRemove,
    errors,
    disabled,
    translations,
    isRTL
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const calculateTotal = (price, quantity) => {
        const p = parseFloat(price) || 0;
        const q = parseFloat(quantity) || 0;
        return (p * q).toFixed(2);
    };

    if (isMobile) {
        return (
            <div className="items-mobile-container">
                <div className="items-header-mobile">
                    <h4>
                        <FontAwesomeIcon icon={faBoxes} className="header-icon-only" />
                        {translations?.returnItems || 'Return Items'}
                    </h4>
                </div>
                <div className="items-cards-container">
                    {items.map((item, index) => (
                        <div key={index} className="item-card-mobile">
                            <div className="item-card-header">
                                <span className="item-number">
                                    <FontAwesomeIcon icon={faBox} className="header-icon-only" />
                                    {item.product_name}
                                </span>
                                <button
                                    type="button"
                                    className="item-remove-btn-mobile"
                                    onClick={() => onRemove(index)}
                                    disabled={disabled}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>

                            <div className="item-card-content">
                                <div className="mobile-field">
                                    <label>
                                        <FontAwesomeIcon icon={faTag} className="field-icon" />
                                        {translations?.batchNumber || 'Batch'}
                                    </label>
                                    <input
                                        type="text"
                                        value={item.batch_no}
                                        readOnly
                                        className="readonly-input"
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                </div>

                                <div className="mobile-field">
                                    <label>{translations?.unit || 'Unit'}</label>
                                    <input
                                        type="text"
                                        value={item.unit}
                                        readOnly
                                        className="readonly-input"
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                </div>

                                <div className="mobile-row">
                                    <div className={`mobile-field half ${errors[`quantity_${index}`] ? 'has-error' : ''}`}>
                                        <label>{translations?.quantityToReturn || 'Qty to Return'} *</label>
                                        <input
                                            type="number"
                                            value={item.return_quantity || ''}
                                            onChange={(e) => onQuantityChange(index, e.target.value)}
                                            step="0.001"
                                            min="0.001"
                                            max={item.available_for_return}
                                            placeholder="0.000"
                                            disabled={disabled}
                                            dir="ltr"
                                        />
                                        <small className="stock-hint">
                                            {translations?.available || 'Available'}: {item.available_for_return} {item.unit}
                                        </small>
                                    </div>

                                    <div className="mobile-field half">
                                        <label>{translations?.price || 'Price'}</label>
                                        <input
                                            type="text"
                                            value={item.price}
                                            readOnly
                                            className="readonly-input"
                                        />
                                    </div>
                                </div>

                                <div className="mobile-row">
                                    <div className="mobile-field half">
                                        <label>{translations?.total || 'Total'}</label>
                                        <input
                                            type="text"
                                            value={calculateTotal(item.price, item.return_quantity)}
                                            readOnly
                                            disabled
                                            className="readonly-input total-display"
                                        />
                                    </div>
                                </div>

                                {errors[`quantity_${index}`] && (
                                    <div className="error-text">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors[`quantity_${index}`]}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="items-table-container">
            <div className="table-header-actions">
                <h4>
                    <FontAwesomeIcon icon={faBoxes} className="header-icon-only" />
                    {translations?.returnItems || 'Return Items'}
                </h4>
            </div>
            <div className="table-wrapper">
                <table className="items-table">
                    <thead>
                        <tr>
                            <th className="product-col">{translations?.productName || 'Product'}</th>
                            <th className="batch-col">{translations?.batchNumber || 'Batch'}</th>
                            <th className="unit-col">{translations?.unit || 'Unit'}</th>
                            <th className="price-col">{translations?.price || 'Price'}</th>
                            <th className="qty-col">{translations?.quantityToReturn || 'Qty to Return'}</th>
                            <th className="total-col">{translations?.total || 'Total'}</th>
                            <th className="action-col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={index} className={errors[`quantity_${index}`] ? 'error-row' : ''}>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="text"
                                            value={item.product_name}
                                            readOnly
                                            className="readonly-input"
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        {item.barcode && (
                                            <small className="barcode-display">
                                                <FontAwesomeIcon icon={faBarcode} size="xs" />
                                                {item.barcode}
                                            </small>
                                        )}
                                    </div>
                                </td>
                                <tr>
                                    <div className="table-field">
                                        <input
                                            type="text"
                                            value={item.batch_no}
                                            readOnly
                                            className="readonly-input"
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>
                                </tr>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="text"
                                            value={item.unit}
                                            readOnly
                                            className="readonly-input"
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="number"
                                            value={item.price}
                                            readOnly
                                            className="readonly-input"
                                        />
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="number"
                                            value={item.return_quantity || ''}
                                            onChange={(e) => onQuantityChange(index, e.target.value)}
                                            step="0.001"
                                            min="0.001"
                                            max={item.available_for_return}
                                            placeholder="0.000"
                                            disabled={disabled}
                                            dir="ltr"
                                            className={errors[`quantity_${index}`] ? 'error' : ''}
                                        />
                                        <small className="stock-hint">
                                            {translations?.available || 'Available'}: {item.available_for_return} {item.unit}
                                        </small>
                                        {errors[`quantity_${index}`] && (
                                            <div className="table-error">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {errors[`quantity_${index}`]}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="text"
                                            value={calculateTotal(item.price, item.return_quantity)}
                                            readOnly
                                            disabled
                                            className="readonly-input total-display"
                                        />
                                    </div>
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        className="table-remove-btn"
                                        onClick={() => onRemove(index)}
                                        disabled={disabled}
                                        title={translations?.remove || 'Remove'}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ==============================================
// MODERN RECEIPT MODAL COMPONENT (Enhanced with translations)
// ==============================================
const ReceiptModal = ({ show, returnData, businessInfo, shopInfo, translations, onPrint, onClose }) => {
    const receiptRef = useRef(null);
    
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return new Date().toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        }
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatTime = () => {
        return new Date().toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    const handlePrint = () => {
        if (receiptRef.current) {
            const printContent = receiptRef.current.innerHTML;
            const printWindow = window.open('', '_blank');
            if (printWindow) {
                printWindow.document.write('<html><head><title>Return Receipt</title>');
                printWindow.document.write('<style>');
                printWindow.document.write(`
                    @media print {
                        @page {
                            size: 80mm auto;
                            margin: 0;
                        }
                        body {
                            margin: 0;
                            padding: 2mm;
                            font-family: 'Courier New', monospace;
                            background: white;
                            color: black;
                            width: 80mm;
                        }
                    }
                    .receipt-print {
                        width: 80mm;
                        margin: 0 auto;
                        padding: 2mm;
                        font-family: 'Courier New', monospace;
                        font-size: 10pt;
                    }
                    .text-center { text-align: center; }
                    .text-right { text-align: right; }
                    .font-bold { font-weight: bold; }
                    .border-bottom { border-bottom: 1px dashed #000; }
                    .border-top { border-top: 1px dashed #000; }
                    .mb-1 { margin-bottom: 1mm; }
                    .mb-2 { margin-bottom: 2mm; }
                    .mt-1 { margin-top: 1mm; }
                    .mt-2 { margin-top: 2mm; }
                    .pt-1 { padding-top: 1mm; }
                    .pb-1 { padding-bottom: 1mm; }
                    table { width: 100%; border-collapse: collapse; }
                    th { text-align: left; border-bottom: 1px solid #000; }
                    td { padding: 0.5mm 0; }
                `);
                printWindow.document.write('</style></head><body>');
                printWindow.document.write('<div class="receipt-print">');
                printWindow.document.write(printContent);
                printWindow.document.write('</div>');
                printWindow.document.write('</body></html>');
                printWindow.document.close();
                
                setTimeout(() => {
                    printWindow.print();
                    printWindow.onafterprint = () => {
                        printWindow.close();
                    };
                }, 300);
            }
        }
        if (onPrint) onPrint();
    };

    if (!show || !returnData) return null;

    const shopName = shopInfo?.name || businessInfo?.businessname || 'Shop Name';
    const shopAddress = businessInfo?.businessaddress || '';
    const shopPhone = businessInfo?.phonenumber || '';

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content receipt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                <div className="modal-header">
                    <h3>
                        <FontAwesomeIcon icon={faReceipt} />
                        {translations?.returnReceipt || 'Return Receipt'}
                    </h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <div ref={receiptRef} className="receipt-preview" style={{ 
                        fontFamily: "'Courier New', monospace", 
                        fontSize: '10pt',
                        padding: '10px',
                        background: '#f8f9fa',
                        borderRadius: '8px'
                    }}>
                        {/* Header */}
                        <div className="text-center mb-2">
                            <div style={{ fontSize: '14pt', fontWeight: 'bold' }}>{shopName}</div>
                            {shopAddress && <div style={{ fontSize: '8pt' }}>{shopAddress}</div>}
                            {shopPhone && <div style={{ fontSize: '8pt' }}>Tel: {shopPhone}</div>}
                        </div>

                        {/* Title */}
                        <div className="text-center font-bold mb-2" style={{ fontSize: '12pt', textTransform: 'uppercase' }}>
                            {translations?.returnReceipt || 'RETURN RECEIPT'}
                        </div>

                        {/* Return Info */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '8pt', marginBottom: '1mm' }}>
                            <span>{translations?.returnNumber || 'Return #'}: {returnData?.return_number || 'N/A'}</span>
                            <span>{translations?.date || 'Date'}: {formatDate(returnData?.date)}</span>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '8pt', marginBottom: '2mm' }}>
                            <span>{translations?.time || 'Time'}: {formatTime()}</span>
                            <span>{translations?.cashier || 'Cashier'}: {returnData?.username || 'Staff'}</span>
                        </div>

                        {/* Original Invoice */}
                        <div className="border-bottom pb-1 mb-2" style={{ fontSize: '8pt' }}>
                            <span>{translations?.originalBillNumber || 'Original Invoice'}: {returnData?.original_bill_number || returnData?.bill_number || 'N/A'}</span>
                        </div>

                        {/* Customer Info */}
                        {(returnData?.customer_name || returnData?.customer_phone) && (
                            <div className="border-top border-bottom pt-1 pb-1 mb-2" style={{ fontSize: '8pt' }}>
                                {returnData?.customer_name && <div><strong>{returnData.customer_name}</strong></div>}
                                {returnData?.customer_phone && <div>{translations?.phone || 'Phone'}: {returnData.customer_phone}</div>}
                            </div>
                        )}

                        {/* Items Table */}
                        <table style={{ fontSize: '8pt', marginBottom: '2mm' }}>
                            <thead>
                                <tr>
                                    <th>{translations?.productName || 'Item'}</th>
                                    <th className="text-right">{translations?.quantity || 'Qty'}</th>
                                    <th className="text-right">{translations?.price || 'Price'}</th>
                                    <th className="text-right">{translations?.total || 'Total'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {returnData?.items?.map((item, index) => (
                                    <tr key={index}>
                                        <td>
                                            {item.product_name}
                                            {item.batch_no && <div style={{fontSize: '7pt'}}>{translations?.batchNumber || 'Batch'}: {item.batch_no}</div>}
                                        </td>
                                        <td className="text-right">{item.quantity}</td>
                                        <td className="text-right">{parseFloat(item.price).toFixed(2)}</td>
                                        <td className="text-right">Rs.{parseFloat(item.total || item.price * item.quantity).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Totals */}
                        <div className="border-top pt-1 mb-1">
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9pt', padding: '0.5mm 0' }}>
                                <span>{translations?.subtotal || 'Subtotal'}:</span>
                                <span>Rs. {parseFloat(returnData?.subtotal || 0).toFixed(2)}</span>
                            </div>
                            
                            {parseFloat(returnData?.tax) > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9pt', padding: '0.5mm 0' }}>
                                    <span>{translations?.tax || 'Tax'}:</span>
                                    <span>Rs. {parseFloat(returnData?.tax).toFixed(2)}</span>
                                </div>
                            )}
                            
                            {parseFloat(returnData?.discount) > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9pt', padding: '0.5mm 0' }}>
                                    <span>{translations?.discount || 'Discount'}:</span>
                                    <span>-Rs. {parseFloat(returnData?.discount).toFixed(2)}</span>
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11pt', fontWeight: 'bold', borderTop: '1px solid #000', paddingTop: '1mm', marginTop: '1mm' }}>
                                <span>{translations?.grandTotal || 'Grand Total'}:</span>
                                <span>Rs. {parseFloat(returnData?.grand_total || 0).toFixed(2)}</span>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11pt', fontWeight: 'bold', color: '#dc2626', marginTop: '1mm' }}>
                                <span>{translations?.refundAmount || 'Refund Amount'}:</span>
                                <span>Rs. {parseFloat(returnData?.refund_amount || 0).toFixed(2)}</span>
                            </div>
                        </div>

                        {/* Payment Info */}
                        <div className="text-center" style={{ fontSize: '8pt', margin: '1mm 0' }}>
                            {translations?.refundMethod || 'Refund Method'}: {returnData?.payment_method?.toUpperCase() || 'CASH'}
                        </div>

                        {/* Reason */}
                        {returnData?.return_reason && (
                            <div className="text-center" style={{ fontSize: '7pt', margin: '1mm 0', borderTop: '1px dashed #000', paddingTop: '1mm' }}>
                                <div><strong>{translations?.returnReason || 'Reason'}:</strong> {returnData.return_reason}</div>
                            </div>
                        )}

                        {/* Notes */}
                        {returnData?.notes && (
                            <div className="text-center" style={{ fontSize: '7pt', margin: '1mm 0' }}>
                                {returnData.notes}
                            </div>
                        )}

                        {/* Footer */}
                        <div className="text-center border-top pt-1 mt-2" style={{ fontSize: '7pt' }}>
                            <div style={{ fontSize: '9pt', fontWeight: 'bold', margin: '1mm 0' }}>
                                {translations?.thankYou || 'Thank you for your business!'}
                            </div>
                            <div>{translations?.poweredBy || 'Powered by OurZest'}</div>
                        </div>
                    </div>
                </div>

                <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '15px', borderTop: '1px solid #eee' }}>
                    <button 
                        className="btn-secondary" 
                        onClick={onClose}
                        style={{ padding: '8px 15px' }}
                    >
                        <FontAwesomeIcon icon={faTimes} style={{ marginRight: '5px' }} />
                        {translations?.close || 'Close'}
                    </button>
                    <button 
                        className="btn-primary" 
                        onClick={handlePrint}
                        style={{ padding: '8px 15px', background: 'var(--purchase-primary-color)', color: 'white', border: 'none', borderRadius: '4px' }}
                    >
                        <FontAwesomeIcon icon={faPrint} style={{ marginRight: '5px' }} />
                        {translations?.print || 'Print'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Business Info Component
// ==============================================
const BusinessInfo = ({ businessInfo, translations }) => {
    if (!businessInfo) return null;

    return (
        <div className="business-info-card" style={{ 
            background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
            borderRadius: '12px',
            padding: '15px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '15px'
        }}>
            {businessInfo.bussinesslogo && (
                <img 
                    src={businessInfo.bussinesslogo} 
                    alt="Logo" 
                    style={{ 
                        width: '50px', 
                        height: '50px', 
                        objectFit: 'contain',
                        borderRadius: '8px'
                    }}
                />
            )}
            <div className="business-details">
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
                    {businessInfo.businessname}
                </div>
                {businessInfo.businessaddress && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} size="xs" style={{ marginRight: '5px' }} />
                        {businessInfo.businessaddress}
                    </div>
                )}
                {businessInfo.phonenumber && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        <FontAwesomeIcon icon={faPhone} size="xs" style={{ marginRight: '5px' }} />
                        {businessInfo.phonenumber}
                    </div>
                )}
            </div>
        </div>
    );
};

// ==============================================
// Main Return Component (Enhanced)
// ==============================================
const Return = () => {
    const { user, currentShop } = useAuth();

    const [shopId, setShopId] = useState(() => {
        if (user?.shop_id) {
            return user.shop_id;
        }
        return localStorage.getItem('current_shop_id') || '';
    });

    const [shopInfo, setShopInfo] = useState({
        id: shopId,
        name: currentShop?.shop_name || localStorage.getItem('current_shop_name') || ''
    });

    const [businessInfo, setBusinessInfo] = useState(null);

    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    const [searching, setSearching] = useState(false);
    const [invoiceFound, setInvoiceFound] = useState(false);
    const [originalSale, setOriginalSale] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        return_number: '',
        return_number_int: 0,
        sell_id: null,
        date: new Date().toISOString().split('T')[0],
        customer_id: null,
        customer_name: '',
        customer_phone: '',
        subtotal: '0.00',
        tax: '0.00',
        discount: '0.00',
        grand_total: '0.00',
        refund_amount: '0.00',
        payment_method: 'cash',
        return_reason: '',
        notes: '',
        items: [],
        themeColor: localStorage.getItem('ourzest_theme') || '#8b5cf6'
    });

    const [todaySummary, setTodaySummary] = useState({
        total_returns: 0,
        total_amount: 0,
        total_refund: 0
    });

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);

    // Receipt modal state
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [currentReturn, setCurrentReturn] = useState(null);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);
    const submitTimeoutRef = useRef(null);
    const requestIdRef = useRef(0);

    // Load translations
    const loadTranslations = useCallback(async (lang) => {
        if (!lang) return;

        setIsTranslating(true);
        try {
            const trans = await returnTranslator.getTranslations(lang);
            setTranslations(trans);

            const isRTL = lang === 'ur' || lang === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';

        } catch (error) {
            console.error('Error loading translations:', error);
        } finally {
            setIsTranslating(false);
        }
    }, []);

    // Setup language change listener
    useEffect(() => {
        loadTranslations(currentLanguage);

        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
                loadTranslations(newLang);
            }
        };

        window.addEventListener('storage', handleLanguageChange);

        const intervalId = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 500);

        const translationListener = (lang) => {
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        returnTranslator.addListener(translationListener);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(intervalId);
            returnTranslator.removeListener(translationListener);
        };
    }, [currentLanguage, loadTranslations]);

    // Show toast function
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    }, [currentLanguage]);

    // Clean up timers
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
            if (submitTimeoutRef.current) {
                clearTimeout(submitTimeoutRef.current);
            }
        };
    }, []);

    // Load business info
    const loadBusinessInfo = useCallback(async () => {
        if (!user?.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo(data.data);
            }
        } catch (error) {
            console.error('Error loading business info:', error);
        }
    }, [user?.username]);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'purchase-primary-color': color,
                    'purchase-primary-dark': adjust(-40),
                    'purchase-primary-light': adjust(40),
                    'purchase-primary-ultralight': adjust(20),
                    'purchase-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'purchase-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(formData.themeColor);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== formData.themeColor) {
                setFormData(prev => ({ ...prev, themeColor: theme }));
                applyTheme(theme);
            }
        };

        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [formData.themeColor]);

    // Update shopInfo when currentShop changes
    useEffect(() => {
        if (currentShop) {
            setShopInfo({
                id: currentShop.shop_id,
                name: currentShop.shop_name
            });
        }
    }, [currentShop]);

    // Get next return number on mount
    useEffect(() => {
        if (shopId) {
            getNextReturnNumber();
        }
        loadBusinessInfo();
    }, [shopId, loadBusinessInfo]);

    // Load today's summary
    useEffect(() => {
        if (shopId && user?.username) {
            loadTodaySummary();
        }
    }, [shopId, user?.username]);

    // Calculate totals when items change
    useEffect(() => {
        calculateTotals();
    }, [formData.items, formData.tax, formData.discount]);

    const getNextReturnNumber = async () => {
        if (!shopId) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/return.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_next_return_number',
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setFormData(prev => ({
                    ...prev,
                    return_number: data.data.return_number,
                    return_number_int: data.data.return_number_int
                }));
            }
        } catch (error) {
            console.error('Error getting next return number:', error);
        }
    };

    const loadTodaySummary = async () => {
        if (!user?.username || !shopId) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/return.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_today_summary',
                    username: user.username,
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setTodaySummary(data.data);
            }
        } catch (error) {
            console.error('Error loading today summary:', error);
        }
    };

    const searchInvoice = async () => {
        if (!searchTerm.trim()) {
            showToast('error', translations?.searchPlaceholder || 'Please enter an invoice number');
            return;
        }

        if (!shopId) {
            showToast('error', translations?.noShopSelected || 'No shop selected');
            return;
        }

        setSearching(true);
        setInvoiceFound(false);
        setOriginalSale(null);
        setFormData(prev => ({
            ...prev,
            sell_id: null,
            customer_id: null,
            customer_name: '',
            customer_phone: '',
            subtotal: '0.00',
            tax: '0.00',
            discount: '0.00',
            grand_total: '0.00',
            refund_amount: '0.00',
            items: []
        }));

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/return.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'search_invoice',
                    search_term: searchTerm,
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success' && data.data) {
                setInvoiceFound(true);
                setOriginalSale(data.data);
                
                // Initialize return items with zero quantity
                const returnItems = data.data.items.map(item => ({
                    ...item,
                    return_quantity: '',
                    sell_item_id: item.id
                }));

                setFormData(prev => ({
                    ...prev,
                    sell_id: data.data.id,
                    customer_id: data.data.customer_id,
                    customer_name: data.data.customer_name || (data.data.customer_details_name || ''),
                    customer_phone: data.data.customer_phone || (data.data.customer_details_phone || ''),
                    subtotal: '0.00',
                    grand_total: '0.00',
                    refund_amount: '0.00',
                    items: returnItems
                }));

                showToast('success', `${translations?.invoiceFound || 'Invoice found'}: #${data.data.bill_number}`);
            } else {
                showToast('error', data.message || translations?.invoiceNotFound || 'Invoice not found');
            }
        } catch (error) {
            console.error('Error searching invoice:', error);
            showToast('error', translations?.networkError || 'Network error occurred');
        } finally {
            setSearching(false);
        }
    };

    const handleQuantityChange = (index, value) => {
        const updatedItems = [...formData.items];
        const item = updatedItems[index];
        
        const qty = value === '' ? '' : parseFloat(value);
        
        // Validate quantity
        if (qty !== '' && (isNaN(qty) || qty < 0)) {
            return;
        }

        if (qty !== '' && qty > item.available_for_return) {
            setErrors(prev => ({
                ...prev,
                [`quantity_${index}`]: `${translations?.quantityExceedsAvailable || 'Cannot return more than'} ${item.available_for_return} ${item.unit}`
            }));
            return;
        }

        // Clear error if valid
        if (errors[`quantity_${index}`]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`quantity_${index}`];
                return newErrors;
            });
        }

        item.return_quantity = qty === '' ? '' : qty;
        setFormData(prev => ({
            ...prev,
            items: updatedItems
        }));
    };

    const removeItem = (index) => {
        if (formData.items.length > 1) {
            const updatedItems = [...formData.items];
            updatedItems.splice(index, 1);
            setFormData(prev => ({
                ...prev,
                items: updatedItems
            }));

            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`quantity_${index}`];
                return newErrors;
            });
        }
    };

    const calculateTotals = () => {
        let subtotal = 0;
        let hasAnyReturn = false;

        formData.items.forEach(item => {
            if (item.return_quantity && parseFloat(item.return_quantity) > 0) {
                hasAnyReturn = true;
                const price = parseFloat(item.price) || 0;
                const quantity = parseFloat(item.return_quantity) || 0;
                subtotal += price * quantity;
            }
        });

        const tax = parseFloat(formData.tax) || 0;
        const discount = parseFloat(formData.discount) || 0;
        const grandTotal = subtotal + tax - discount;
        
        // Refund amount is typically the grand total of returned items
        const refundAmount = grandTotal;

        setFormData(prev => ({
            ...prev,
            subtotal: subtotal.toFixed(2),
            grand_total: grandTotal.toFixed(2),
            refund_amount: refundAmount.toFixed(2)
        }));

        return hasAnyReturn;
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.sell_id) {
            newErrors.invoice = translations?.invoiceNotFound || 'Please search and select an invoice first';
        }

        if (!formData.items || formData.items.length === 0) {
            newErrors.items = translations?.itemsRequired || 'At least one item is required for return';
        } else {
            let hasValidReturn = false;
            
            formData.items.forEach((item, index) => {
                const qty = parseFloat(item.return_quantity) || 0;
                
                if (qty > 0) {
                    hasValidReturn = true;
                    
                    if (qty > item.available_for_return) {
                        newErrors[`quantity_${index}`] = `${translations?.quantityExceedsAvailable || 'Cannot return more than'} ${item.available_for_return} ${item.unit}`;
                    }
                    
                    if (qty <= 0) {
                        newErrors[`quantity_${index}`] = translations?.invalidQuantity || 'Please enter a valid quantity';
                    }
                }
            });

            if (!hasValidReturn) {
                newErrors.items = translations?.itemsRequired || 'Please enter at least one item to return';
            }
        }

        if (!formData.return_reason.trim()) {
            newErrors.return_reason = translations?.returnReasonRequired || 'Return reason is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handlePrintFromModal = () => {
        setShowReceiptModal(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Prevent multiple submissions
        if (isSubmitting || saving) {
            console.log('Submission already in progress');
            return;
        }

        if (!shopId) {
            showToast('error', translations?.noShopSelected || 'No shop selected. Please select a shop first.');
            return;
        }

        if (!validateForm()) {
            const firstError = document.querySelector('.has-error, .error-message-modern');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        setSaving(true);
        setIsSubmitting(true);

        // Clear any existing timeout
        if (submitTimeoutRef.current) {
            clearTimeout(submitTimeoutRef.current);
        }

        // Generate unique request ID
        const requestId = ++requestIdRef.current;

        try {
            const currentUsername = user?.username || '';
            if (!currentUsername) {
                throw new Error("User not authenticated");
            }

            // Filter items that have return quantity > 0
            const returnItems = formData.items
                .filter(item => parseFloat(item.return_quantity) > 0)
                .map(item => ({
                    sell_item_id: item.sell_item_id || item.id,
                    product_id: item.product_id,
                    product_name: item.product_name,
                    batch_no: item.batch_no,
                    barcode: item.barcode,
                    unit: item.unit,
                    quantity: parseFloat(item.return_quantity),
                    price: parseFloat(item.price),
                    total: parseFloat(item.price) * parseFloat(item.return_quantity)
                }));

            const payload = {
                action: 'add',
                username: currentUsername,
                shop_id: shopId,
                sell_id: formData.sell_id,
                date: formData.date,
                customer_id: formData.customer_id,
                customer_name: formData.customer_name,
                customer_phone: formData.customer_phone,
                subtotal: parseFloat(formData.subtotal),
                tax: parseFloat(formData.tax),
                discount: parseFloat(formData.discount),
                grand_total: parseFloat(formData.grand_total),
                refund_amount: parseFloat(formData.refund_amount),
                payment_method: formData.payment_method,
                return_reason: formData.return_reason,
                notes: formData.notes,
                items: returnItems,
                timestamp: Date.now(),
                request_id: requestId
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/return.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', translations?.returnProcessed || 'Return processed successfully!');

                // Set current return for receipt modal
                const returnForReceipt = {
                    ...data.data,
                    items: returnItems,
                    subtotal: formData.subtotal,
                    tax: formData.tax,
                    discount: formData.discount,
                    grand_total: formData.grand_total,
                    refund_amount: formData.refund_amount,
                    payment_method: formData.payment_method,
                    return_reason: formData.return_reason,
                    notes: formData.notes,
                    customer_name: formData.customer_name,
                    customer_phone: formData.customer_phone,
                    username: currentUsername,
                    original_bill_number: originalSale?.bill_number
                };
                
                setCurrentReturn(returnForReceipt);

                // Reset form
                setFormData(prev => ({
                    ...prev,
                    date: new Date().toISOString().split('T')[0],
                    customer_id: null,
                    customer_name: '',
                    customer_phone: '',
                    subtotal: '0.00',
                    tax: '0.00',
                    discount: '0.00',
                    grand_total: '0.00',
                    refund_amount: '0.00',
                    payment_method: 'cash',
                    return_reason: '',
                    notes: '',
                    items: []
                }));

                setSearchTerm('');
                setInvoiceFound(false);
                setOriginalSale(null);
                setErrors({});

                getNextReturnNumber();
                loadTodaySummary();

                // Show receipt modal after successful return
                setTimeout(() => {
                    setShowReceiptModal(true);
                }, 500);

            } else if (data.duplicate_prevention) {
                showToast('warning', data.message || 'Duplicate submission prevented');
                // Wait a bit before allowing new submission
                submitTimeoutRef.current = setTimeout(() => {
                    setIsSubmitting(false);
                }, 3000);
            } else {
                showToast('error', data.message || translations?.failedProcess || 'Failed to process return');
            }
        } catch (error) {
            console.error('Submission error:', error);
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            setSaving(false);
            // Only reset submitting if not in cooldown
            if (!submitTimeoutRef.current) {
                setIsSubmitting(false);
            }
        }
    };

    const handleClear = () => {
        setFormData(prev => ({
            ...prev,
            date: new Date().toISOString().split('T')[0],
            customer_id: null,
            customer_name: '',
            customer_phone: '',
            subtotal: '0.00',
            tax: '0.00',
            discount: '0.00',
            grand_total: '0.00',
            refund_amount: '0.00',
            payment_method: 'cash',
            return_reason: '',
            notes: '',
            items: []
        }));
        setSearchTerm('');
        setInvoiceFound(false);
        setOriginalSale(null);
        setErrors({});
        getNextReturnNumber();
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return 'Rs. 0.00';
        return `Rs. ${parseFloat(amount).toFixed(2)}`;
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`purchases-form-independent ${isRTL ? 'rtl' : ''} ${isTranslating ? 'translating' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Receipt Modal */}
            <ReceiptModal
                show={showReceiptModal}
                returnData={currentReturn}
                businessInfo={businessInfo}
                shopInfo={shopInfo}
                translations={translations}
                onPrint={handlePrintFromModal}
                onClose={() => setShowReceiptModal(false)}
            />

            {/* Modern Form Header */}
            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faUndo} />
                    </div>
                    <div className="header-text">
                        <h1>{translations?.processReturn || 'Process Return'}</h1>
                        <p>{translations?.returnItems || 'Search invoice and return items'}</p>
                    </div>
                </div>
                {isTranslating && (
                    <div className="translation-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Translating...</span>
                    </div>
                )}
            </div>

            {/* Business Info */}
            <BusinessInfo businessInfo={businessInfo} translations={translations} />

            {/* Modern Form */}
            <form onSubmit={handleSubmit} className="modern-form">
                {/* Invoice Search Section */}
                <div className="form-section">
                    <div className="section-header">
                        <h3 className="section-title">
                            <FontAwesomeIcon icon={faSearch} />
                            {translations?.searchInvoice || 'Search Invoice'}
                        </h3>
                    </div>

                    <div className="form-grid-modern">
                        <div className="form-group-modern search-group">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faSearch} className="input-icon search-icon" />
                                <input
                                    type="text"
                                    id="invoice_search"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && searchInvoice()}
                                    placeholder=" "
                                    disabled={saving || isSubmitting}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                    className="product-search-input"
                                    autoComplete="off"
                                />
                                <label htmlFor="invoice_search" className="input-label">
                                    {translations?.searchPlaceholder || 'Enter Invoice, Customer Name or Phone'}
                                </label>
                                <button
                                    type="button"
                                    className="search-btn"
                                    onClick={searchInvoice}
                                    disabled={searching || saving || isSubmitting}
                                >
                                    {searching ? (
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSearch} />
                                            {translations?.searchInvoice || 'Search'}
                                        </>
                                    )}
                                </button>
                            </div>
                            {errors.invoice && (
                                <div className="error-message-modern">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    {errors.invoice}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {invoiceFound && originalSale && (
                    <>
                        {/* Return Information Section */}
                        <div className="form-section">
                            <div className="section-header">
                                <h3 className="section-title">
                                    <FontAwesomeIcon icon={faFileInvoice} />
                                    {translations?.returnInformation || 'Return Information'}
                                </h3>
                            </div>

                            <div className="form-grid-modern">
                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faFileInvoice} className="input-icon" />
                                        <input
                                            type="text"
                                            id="return_number"
                                            value={formData.return_number}
                                            readOnly
                                            className="readonly-input"
                                            dir="ltr"
                                            placeholder=" "
                                        />
                                        <label htmlFor="return_number" className="input-label">
                                            {translations?.returnNumber || 'Return Number'}
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                        <input
                                            type="date"
                                            id="date"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleChange}
                                            disabled={saving || isSubmitting}
                                            dir="ltr"
                                            placeholder=" "
                                        />
                                        <label htmlFor="date" className="input-label">
                                            {translations?.returnDate || 'Return Date'}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Original Invoice Info */}
                        <div className="form-section">
                            <div className="section-header">
                                <h3 className="section-title">
                                    <FontAwesomeIcon icon={faHistory} />
                                    {translations?.originalInvoice || 'Original Invoice'}
                                </h3>
                            </div>

                            <div className="form-grid-modern">
                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faFileInvoice} className="input-icon" />
                                        <input
                                            type="text"
                                            value={originalSale.bill_number}
                                            readOnly
                                            className="readonly-input"
                                            placeholder=" "
                                        />
                                        <label className="input-label">
                                            {translations?.originalBillNumber || 'Original Bill Number'}
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                        <input
                                            type="text"
                                            value={originalSale.date}
                                            readOnly
                                            className="readonly-input"
                                            placeholder=" "
                                        />
                                        <label className="input-label">
                                            {translations?.originalDate || 'Original Date'}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Customer Information */}
                        <div className="form-section">
                            <div className="section-header">
                                <h3 className="section-title">
                                    <FontAwesomeIcon icon={faUser} />
                                    {translations?.customerDetails || 'Customer Details'}
                                </h3>
                            </div>

                            <div className="form-grid-modern">
                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faUser} className="input-icon" />
                                        <input
                                            type="text"
                                            value={formData.customer_name}
                                            readOnly
                                            className="readonly-input"
                                            placeholder=" "
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label className="input-label">
                                            {translations?.customerName || 'Customer Name'}
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faPhone} className="input-icon" />
                                        <input
                                            type="text"
                                            value={formData.customer_phone}
                                            readOnly
                                            className="readonly-input"
                                            placeholder=" "
                                        />
                                        <label className="input-label">
                                            {translations?.customerPhone || 'Phone'}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Items Section */}
                        {formData.items.length > 0 && (
                            <div className="form-section">
                                <ReturnItemsTable
                                    items={formData.items}
                                    onQuantityChange={handleQuantityChange}
                                    onRemove={removeItem}
                                    errors={errors}
                                    disabled={saving || isSubmitting}
                                    translations={translations}
                                    isRTL={isRTL}
                                />
                                {errors.items && (
                                    <div className="error-message-modern" style={{ marginTop: '10px' }}>
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.items}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Calculations Section */}
                        {formData.items.some(item => parseFloat(item.return_quantity) > 0) && (
                            <div className="form-section">
                                <div className="section-header">
                                    <h3 className="section-title">
                                        <FontAwesomeIcon icon={faCalculator} />
                                        {translations?.returnCalculations || 'Return Calculations'}
                                    </h3>
                                </div>

                                <div className="calculations-grid">
                                    <div className="calculation-row">
                                        <span className="calculation-label">{translations?.subtotal || 'Subtotal'}:</span>
                                        <span className="calculation-value">Rs. {formData.subtotal}</span>
                                    </div>

                                    <div className="calculation-row">
                                        <span className="calculation-label">{translations?.tax || 'Tax'}:</span>
                                        <div className="tax-input-wrapper" style={{ width: '120px' }}>
                                            <input
                                                type="number"
                                                id="tax"
                                                name="tax"
                                                value={formData.tax}
                                                onChange={handleChange}
                                                step="0.01"
                                                min="0"
                                                placeholder="0.00"
                                                disabled={saving || isSubmitting}
                                                dir="ltr"
                                                style={{ textAlign: 'right' }}
                                            />
                                        </div>
                                    </div>

                                    <div className="calculation-row">
                                        <span className="calculation-label">{translations?.discount || 'Discount'}:</span>
                                        <div className="discount-input-wrapper" style={{ width: '120px' }}>
                                            <input
                                                type="number"
                                                id="discount"
                                                name="discount"
                                                value={formData.discount}
                                                onChange={handleChange}
                                                step="0.01"
                                                min="0"
                                                placeholder="0.00"
                                                disabled={saving || isSubmitting}
                                                dir="ltr"
                                                style={{ textAlign: 'right' }}
                                            />
                                        </div>
                                    </div>

                                    <div className="calculation-row grand-total">
                                        <span className="calculation-label">{translations?.grandTotal || 'Grand Total'}:</span>
                                        <span className="calculation-value highlight">Rs. {formData.grand_total}</span>
                                    </div>

                                    <div className="calculation-row">
                                        <span className="calculation-label">{translations?.refundMethod || 'Refund Method'}:</span>
                                        <div className="payment-method-selector" style={{ width: '120px' }}>
                                            <select
                                                value={formData.payment_method}
                                                onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                                                disabled={saving || isSubmitting}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                                style={{ width: '100%' }}
                                            >
                                                <option value="cash">{translations?.cash || 'Cash'}</option>
                                                <option value="card">{translations?.card || 'Card'}</option>
                                                <option value="upi">{translations?.upi || 'UPI'}</option>
                                                <option value="bank_transfer">{translations?.bankTransfer || 'Bank Transfer'}</option>
                                                <option value="wallet">{translations?.wallet || 'Wallet'}</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="calculation-row refund-amount">
                                        <span className="calculation-label">{translations?.refundAmount || 'Refund Amount'}:</span>
                                        <span className="calculation-value highlight" style={{ color: '#dc2626' }}>
                                            Rs. {formData.refund_amount}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Return Reason Section */}
                        {formData.items.some(item => parseFloat(item.return_quantity) > 0) && (
                            <div className="form-section">
                                <div className="form-group-modern">
                                    <div className="input-container textarea-container">
                                        <FontAwesomeIcon icon={faHistory} className="input-icon" />
                                        <textarea
                                            id="return_reason"
                                            name="return_reason"
                                            value={formData.return_reason}
                                            onChange={handleChange}
                                            placeholder=" "
                                            disabled={saving || isSubmitting}
                                            rows="3"
                                            className={errors.return_reason ? 'error' : ''}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        ></textarea>
                                        <label htmlFor="return_reason" className="input-label">
                                            {translations?.returnReason || 'Return Reason'} *
                                        </label>
                                    </div>
                                    {errors.return_reason && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {errors.return_reason}
                                        </div>
                                    )}
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container textarea-container">
                                        <FontAwesomeIcon icon={faReceipt} className="input-icon" />
                                        <textarea
                                            id="notes"
                                            name="notes"
                                            value={formData.notes}
                                            onChange={handleChange}
                                            placeholder=" "
                                            disabled={saving || isSubmitting}
                                            rows="3"
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        ></textarea>
                                        <label htmlFor="notes" className="input-label">
                                            {translations?.notes || 'Additional Notes'}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Form Actions */}
                        {formData.items.some(item => parseFloat(item.return_quantity) > 0) && (
                            <div className="form-actions-modern">
                                <button
                                    type="button"
                                    className="btn-modern btn-secondary-modern"
                                    onClick={handleClear}
                                    disabled={saving || isSubmitting}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                    <span>{translations?.clear || 'Clear'}</span>
                                </button>
                                <button
                                    type="submit"
                                    className="btn-modern btn-primary-modern"
                                    disabled={saving || isSubmitting || isTranslating}
                                    style={{ background: 'linear-gradient(135deg, #dc2626, #991b1b)' }}
                                >
                                    {saving || isSubmitting ? (
                                        <>
                                            <div className="btn-spinner"></div>
                                            <span>{translations?.saving || 'Processing...'}</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>{translations?.processReturnBtn || 'Process Return'}</span>
                                            <FontAwesomeIcon icon={faUndo} />
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </form>

            {/* Loading Overlay */}
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <p>{translations?.loading || 'Loading...'}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Return;